/**
 * A página do signatário — pública, aberta pelo link pessoal que chegou no WhatsApp ou no e-mail.
 *
 * Três passos, na ordem em que o servidor os exige: ler o documento, confirmar o CPF (o que dispara
 * o código de uso único) e desenhar a assinatura com o código em mãos. Quem não concorda recusa
 * com um motivo, e a recusa encerra o documento para todos.
 *
 * Pensada para o celular: é onde o funcionário recebe o link.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import { QuadroDeAssinatura } from '../../components/assinatura/QuadroDeAssinatura';
import assinaturaService from '../../services/assinaturaService';
import type { CodigoEnviado, ResumoPublico } from '../../types/assinatura';

function mensagemDe(err: unknown, padrao: string): string {
  return err instanceof Error && err.message ? err.message : padrao;
}

function formatarCpf(valor: string): string {
  const d = valor.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

const Moldura: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
    <Paper sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2.5, sm: 4 } }}>{children}</Paper>
  </Box>
);

const Passo: React.FC<{ numero: number; titulo: string; feito?: boolean; children: React.ReactNode }> = ({
  numero,
  titulo,
  feito = false,
  children,
}) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      {feito ? (
        <CheckCircleIcon color="success" fontSize="small" />
      ) : (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {numero}
        </Box>
      )}
      <Typography variant="subtitle1" fontWeight={600}>
        {titulo}
      </Typography>
    </Stack>
    {children}
  </Box>
);

export const AssinarDocumentoPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();

  const [resumo, setResumo] = useState<ResumoPublico | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroFatal, setErroFatal] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [leu, setLeu] = useState(false);
  const [cpf, setCpf] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [codigoEnviado, setCodigoEnviado] = useState<CodigoEnviado | null>(null);

  const [codigo, setCodigo] = useState('');
  const [imagem, setImagem] = useState<string | null>(null);
  const [aceite, setAceite] = useState(false);
  const [assinando, setAssinando] = useState(false);

  const [recusaAberta, setRecusaAberta] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [recusado, setRecusado] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setResumo(await assinaturaService.resumo(token));
    } catch (err: unknown) {
      setErroFatal(mensagemDe(err, 'Não foi possível abrir este link.'));
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const pedirCodigo = useCallback(async () => {
    setErro(null);
    setEnviando(true);
    try {
      setCodigoEnviado(await assinaturaService.pedirCodigo(token, cpf));
      setCodigo('');
    } catch (err: unknown) {
      setErro(mensagemDe(err, 'Não foi possível enviar o código.'));
    } finally {
      setEnviando(false);
    }
  }, [token, cpf]);

  const assinar = useCallback(async () => {
    if (!imagem) return;
    setErro(null);
    setAssinando(true);
    try {
      setResumo(await assinaturaService.assinar(token, { codigo, imagem, aceite }));
    } catch (err: unknown) {
      setErro(mensagemDe(err, 'Não foi possível registrar a assinatura.'));
    } finally {
      setAssinando(false);
    }
  }, [token, codigo, imagem, aceite]);

  const recusar = useCallback(async () => {
    setErro(null);
    try {
      await assinaturaService.recusar(token, motivo);
      setRecusaAberta(false);
      setRecusado(true);
    } catch (err: unknown) {
      setErro(mensagemDe(err, 'Não foi possível registrar a recusa.'));
    }
  }, [token, motivo]);

  if (carregando) {
    return (
      <Moldura>
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </Moldura>
    );
  }

  if (erroFatal || !resumo) {
    return (
      <Moldura>
        <Typography variant="h6" gutterBottom>
          Link de assinatura
        </Typography>
        <Alert severity="warning">{erroFatal ?? 'Link de assinatura inválido.'}</Alert>
      </Moldura>
    );
  }

  const cabecalho = (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" color="text.secondary">
        {resumo.empresa}
      </Typography>
      <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
        {resumo.titulo}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Para {resumo.signatario.nome} · CPF {resumo.signatario.cpfMascarado}
      </Typography>
    </Box>
  );

  if (resumo.status === 'assinado') {
    return (
      <Moldura>
        {cabecalho}
        <Alert severity="success" sx={{ mb: 2 }}>
          Sua assinatura foi registrada.
        </Alert>
        {resumo.documentoStatus === 'assinado' ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Todos assinaram. O documento assinado tem o código de verificação{' '}
              <strong style={{ fontFamily: 'monospace' }}>{resumo.codigoDeVerificacao}</strong>.
            </Typography>
            {resumo.assinadoDisponivel && (
              <Button variant="contained" href={assinaturaService.urlDoAssinado(token)}>
                Baixar documento assinado
              </Button>
            )}
            {resumo.codigoDeVerificacao && (
              <Link href={`/verificar/${resumo.codigoDeVerificacao}`} variant="body2">
                Ver a página de verificação
              </Link>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            O documento ainda aguarda a assinatura de outras pessoas. Quando todos assinarem, este mesmo link passa a
            oferecer o documento assinado.
          </Typography>
        )}
      </Moldura>
    );
  }

  if (recusado || resumo.status === 'recusado') {
    return (
      <Moldura>
        {cabecalho}
        <Alert severity="info">Você recusou este documento. A empresa foi avisada.</Alert>
      </Moldura>
    );
  }

  if (resumo.documentoStatus !== 'pendente') {
    const porque: Record<string, string> = {
      cancelado: 'Este documento foi cancelado pela empresa.',
      expirado: 'O prazo para assinar venceu. Peça à empresa um novo link.',
      recusado: 'Este documento foi recusado por outro signatário e não aceita mais assinaturas.',
      assinado: 'Este documento já foi concluído.',
    };
    return (
      <Moldura>
        {cabecalho}
        <Alert severity="warning">{porque[resumo.documentoStatus] ?? 'Este documento não aceita mais assinaturas.'}</Alert>
      </Moldura>
    );
  }

  const podeAssinar = codigo.replace(/\D/g, '').length === 6 && imagem !== null && aceite && !assinando;

  return (
    <Moldura>
      {cabecalho}

      <Alert severity="info" sx={{ mb: 3 }}>
        Este link é pessoal. O prazo para assinar vai até {new Date(resumo.expiraEm).toLocaleDateString('pt-BR')}.
      </Alert>

      <Passo numero={1} titulo="Leia o documento" feito={leu}>
        <Box
          component="iframe"
          title="Documento para assinar"
          src={assinaturaService.urlDoDocumento(token)}
          sx={{ display: { xs: 'none', md: 'block' }, width: '100%', height: 480, border: 1, borderColor: 'divider', borderRadius: 1, mb: 1.5 }}
        />
        <Button
          variant={leu ? 'text' : 'outlined'}
          startIcon={<OpenInNewIcon />}
          href={assinaturaService.urlDoDocumento(token)}
          target="_blank"
          rel="noopener"
          onClick={() => setLeu(true)}
        >
          Abrir o documento
        </Button>
        {!leu && (
          <Button size="small" sx={{ ml: 1 }} onClick={() => setLeu(true)}>
            Já li
          </Button>
        )}
      </Passo>

      <Passo numero={2} titulo="Confirme o seu CPF" feito={codigoEnviado !== null}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            label="Seu CPF"
            value={cpf}
            onChange={(e) => setCpf(formatarCpf(e.target.value))}
            inputMode="numeric"
            autoComplete="off"
            size="small"
            fullWidth
            disabled={!leu}
          />
          <Button
            variant="contained"
            onClick={() => void pedirCodigo()}
            disabled={!leu || enviando || cpf.replace(/\D/g, '').length !== 11}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {codigoEnviado ? 'Reenviar código' : 'Enviar código'}
          </Button>
        </Stack>
        {codigoEnviado && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enviamos um código por {codigoEnviado.canal} para {codigoEnviado.destino}. Ele vale por{' '}
            {codigoEnviado.expiraEmMinutos} minutos.
          </Typography>
        )}
      </Passo>

      <Passo numero={3} titulo="Assine">
        <Stack spacing={2}>
          <TextField
            label="Código recebido"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            size="small"
            disabled={!codigoEnviado}
            sx={{ maxWidth: 200 }}
            inputProps={{ style: { letterSpacing: 6, fontFamily: 'monospace' } }}
          />
          <QuadroDeAssinatura onChange={setImagem} disabled={!codigoEnviado} />
          <FormControlLabel
            control={<Checkbox checked={aceite} onChange={(e) => setAceite(e.target.checked)} disabled={!codigoEnviado} />}
            label={
              <Typography variant="body2">
                Li o documento e concordo em assiná-lo eletronicamente. Sei que esta assinatura tem validade jurídica
                (Lei 14.063/2020).
              </Typography>
            }
          />
          {erro && <Alert severity="error">{erro}</Alert>}
          <Button variant="contained" size="large" onClick={() => void assinar()} disabled={!podeAssinar}>
            {assinando ? 'Registrando…' : 'Assinar documento'}
          </Button>
        </Stack>
      </Passo>

      <Divider sx={{ my: 2 }} />
      <Button color="inherit" size="small" onClick={() => setRecusaAberta(true)}>
        Não concordo com o documento
      </Button>

      <Dialog open={recusaAberta} onClose={() => setRecusaAberta(false)} fullWidth maxWidth="xs">
        <DialogTitle>Recusar o documento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A recusa encerra o documento para todos os signatários, e a empresa recebe o seu motivo.
          </Typography>
          <TextField
            label="Por que está recusando?"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
          {erro && recusaAberta && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecusaAberta(false)}>Voltar</Button>
          <Button color="error" onClick={() => void recusar()} disabled={motivo.trim().length < 3}>
            Recusar
          </Button>
        </DialogActions>
      </Dialog>
    </Moldura>
  );
};

export default AssinarDocumentoPage;
